import React, { useEffect, useState } from "react";
import {
  api,
  fmtCNPJ,
  fmtRelative,
  formatApiErrorDetail,
  fmtDateTime,
} from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import PageHeader from "@/components/PageHeader";
import StatusBadge from "@/components/StatusBadge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Plus,
  PlayCircle,
  ArrowsCounterClockwise,
  Trash,
  PencilSimple,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { confirmToast } from "@/utils/toastConfirm";

const UFS = [
  "AC",
  "AL",
  "AP",
  "AM",
  "BA",
  "CE",
  "DF",
  "ES",
  "GO",
  "MA",
  "MT",
  "MS",
  "MG",
  "PA",
  "PB",
  "PR",
  "PE",
  "PI",
  "RJ",
  "RN",
  "RS",
  "RO",
  "RR",
  "SC",
  "SP",
  "SE",
  "TO",
];

const emptyForm = {
  cnpj: "",
  razao_social: "",
  uf: "CE",
  ambiente: "mock",
  manifestacao_auto: false,
  download_xml_auto: false,
  ativo: true,
  cert_password: "",
  cert_file: null,
  group_id: "",
};

const Tenants = () => {
  const { isAdmin } = useAuth();
  const [tenants, setTenants] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [submitting, setSubmitting] = useState(false);
  const [extracting, setExtracting] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [t, g] = await Promise.all([
        api.get("/tenants"),
        isAdmin ? api.get("/admin/groups") : Promise.resolve({ data: [] }),
      ]);
      setTenants(t.data);
      setGroups(g.data);
    } catch (e) {
      toast.error("Erro ao carregar empresas");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load(); /* eslint-disable-next-line */
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setOpen(true);
  };

  const openEdit = (t) => {
    setEditing(t);
    setForm({
      cnpj: t.cnpj,
      razao_social: t.razao_social,
      uf: t.uf,
      ambiente: t.ambiente,
      manifestacao_auto: t.manifestacao_auto || false,
      download_xml_auto: t.download_xml_auto || false,
      ativo: t.ativo,
      cert_password: "",
      cert_file: null,
      group_id: t.group_id || "",
    });
    setOpen(true);
  };

  const handleExtractCert = async () => {
    if (!form.cert_file) {
      toast.error("Selecione o arquivo do certificado primeiro.");
      return;
    }

    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("cert_file", form.cert_file);
      fd.append("cert_password", form.cert_password || "");

      const r = await api.post("/tenants/extract-cert", fd);

      setForm((prev) => ({
        ...prev,
        cnpj: r.data.cnpj || prev.cnpj,
        razao_social: r.data.razao_social || prev.razao_social,
      }));

      toast.success(
        `Dados extraídos com sucesso! Válido até: ${fmtDateTime(r.data.valido_ate)}`,
      );
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) ||
          "Falha ao ler o certificado. Verifique a senha.",
      );
    } finally {
      setExtracting(false);
    }
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const fd = new FormData();
      if (!editing) fd.append("cnpj", form.cnpj);
      fd.append("razao_social", form.razao_social);
      fd.append("uf", form.uf);
      fd.append("ambiente", form.ambiente);
      fd.append("manifestacao_auto", form.manifestacao_auto ? "true" : "false");
      fd.append("download_xml_auto", form.download_xml_auto ? "true" : "false");
      fd.append("ativo", form.ativo ? "true" : "false");
      if (form.group_id) fd.append("group_id", form.group_id);
      if (form.cert_password) fd.append("cert_password", form.cert_password);
      if (form.cert_file) fd.append("cert_file", form.cert_file);

      if (editing) {
        await api.patch(`/tenants/${editing.id}`, fd);
        toast.success("Empresa atualizada");
      } else {
        await api.post("/tenants", fd);
        toast.success("Empresa criada");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(
        formatApiErrorDetail(e?.response?.data?.detail) || "Erro ao salvar",
      );
    } finally {
      setSubmitting(false);
    }
  };

  const runNow = async (id) => {
    try {
      await api.post(`/tenants/${id}/run-now`);
      toast.success("Execução disparada");
      setTimeout(load, 1500);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail) || "Erro ao executar");
    }
  };

  const resetNsu = (id) => {
    confirmToast(
      "Resetar ultNSU para 0? Isto irá refazer a captura desde o início.",
      async () => {
        try {
          await api.post(`/tenants/${id}/reset-nsu`);
          toast.success("ultNSU resetado");
          load();
        } catch {
          toast.error("Erro ao resetar");
        }
      },
      "Resetar",
      true,
    );
  };

  const remove = (id) => {
    confirmToast(
      "Excluir esta empresa?",
      async () => {
        try {
          await api.delete(`/tenants/${id}`);
          toast.success("Empresa excluída");
          load();
        } catch {
          toast.error("Erro ao excluir");
        }
      },
      "Excluir",
      true,
    );
  };

  return (
    <div data-testid="page-tenants">
      <PageHeader
        title="Empresas / CNPJs"
        subtitle={
          isAdmin
            ? "Gerencie as empresas e seus respectivos certificados A1. Configure a automação para cada CNPJ e organize o acesso por grupos de clientes."
            : "Visualize os CNPJs vinculados ao seu acesso, acompanhe o status de sincronização e dispare atualizações manuais."
        }
        actions={
          isAdmin && (
            <Button
              onClick={openCreate}
              data-testid="tenant-create-btn"
              className="rounded-sm"
            >
              <Plus size={16} /> Nova Empresa
            </Button>
          )
        }
      />

      <div className="px-8 py-6">
        <div className="bg-white border border-border rounded-sm overflow-x-auto">
          <table className="data-table" data-testid="tenants-table">
            <thead>
              <tr>
                <th>Razão Social</th>
                <th>CNPJ</th>
                <th>Grupo</th>
                <th>UF</th>
                <th>Ambiente</th>
                <th>Status</th>
                <th>Ciência Auto</th>
                <th>XML Auto</th>
                <th>Último NSU</th>
                <th>Último Sync</th>
                <th>Docs</th>
                <th className="text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td
                    colSpan={12}
                    className="text-center py-8 text-muted-foreground"
                  >
                    Carregando...
                  </td>
                </tr>
              )}
              {!loading && tenants.length === 0 && (
                <tr>
                  <td
                    colSpan={12}
                    className="text-center py-12 text-muted-foreground"
                  >
                    <div>Nenhuma empresa cadastrada.</div>
                    {isAdmin && (
                      <Button
                        variant="link"
                        onClick={openCreate}
                        data-testid="empty-create-btn"
                      >
                        Criar primeira empresa
                      </Button>
                    )}
                  </td>
                </tr>
              )}
              {tenants.map((t) => (
                <tr key={t.id} data-testid={`tenant-row-${t.id}`}>
                  <td className="font-medium">{t.razao_social}</td>
                  <td className="font-mono">{fmtCNPJ(t.cnpj)}</td>
                  <td className="text-sm">
                    {groups.find((g) => g.id === t.group_id)?.nome ||
                      (t.group_id ? (
                        <span className="font-mono text-xs">
                          {t.group_id.slice(0, 8)}
                        </span>
                      ) : (
                        <span className="text-muted-foreground text-xs">—</span>
                      ))}
                  </td>
                  <td className="font-mono">{t.uf}</td>
                  <td>
                    <StatusBadge
                      variant={
                        t.ambiente === "producao"
                          ? "success"
                          : t.ambiente === "homologacao"
                            ? "warning"
                            : "info"
                      }
                    >
                      {t.ambiente === "producao"
                        ? "Produção"
                        : t.ambiente === "homologacao"
                          ? "Homologação"
                          : "Mockado"}
                    </StatusBadge>
                  </td>
                  <td>
                    <StatusBadge variant={t.ativo ? "success" : "neutral"}>
                      {t.ativo ? "Ativo" : "Inativo"}
                    </StatusBadge>
                  </td>
                  <td>
                    {t.manifestacao_auto ? (
                      <StatusBadge variant="success">Sim</StatusBadge>
                    ) : (
                      <StatusBadge variant="neutral">Não</StatusBadge>
                    )}
                  </td>
                  <td>
                    {t.download_xml_auto ? (
                      <StatusBadge variant="success">Sim</StatusBadge>
                    ) : (
                      <StatusBadge variant="neutral">Não</StatusBadge>
                    )}
                  </td>
                  <td className="font-mono text-xs">{t.ult_nsu}</td>
                  <td className="text-xs">
                    {fmtRelative(t.ultimo_sync)}
                    {t.recovery_pending_ranges > 0 && (
                      <div className="mt-1 text-muted-foreground">
                        Notas pendentes de recuperação automática
                      </div>
                    )}
                    {t.recovery_unavailable_ranges > 0 && (
                      <div className="mt-1 text-amber-700">
                        Há notas antigas que precisam de revisão
                      </div>
                    )}
                  </td>
                  <td className="font-mono">{t.docs_capturados || 0}</td>
                  <td>
                    <div className="flex items-center justify-end gap-1">
                      <button
                        onClick={() => runNow(t.id)}
                        title="Executar agora"
                        data-testid={`run-now-${t.id}`}
                        className="p-1.5 rounded-sm hover:bg-muted text-foreground"
                      >
                        <PlayCircle size={16} weight="duotone" />
                      </button>
                      {isAdmin && (
                        <>
                          <button
                            onClick={() => resetNsu(t.id)}
                            title="Resetar ultNSU"
                            data-testid={`reset-nsu-${t.id}`}
                            className="p-1.5 rounded-sm hover:bg-muted text-foreground"
                          >
                            <ArrowsCounterClockwise size={16} />
                          </button>
                          <button
                            onClick={() => openEdit(t)}
                            title="Editar"
                            data-testid={`edit-${t.id}`}
                            className="p-1.5 rounded-sm hover:bg-muted text-foreground"
                          >
                            <PencilSimple size={16} />
                          </button>
                          <button
                            onClick={() => remove(t.id)}
                            title="Excluir"
                            data-testid={`delete-${t.id}`}
                            className="p-1.5 rounded-sm hover:bg-muted text-destructive"
                          >
                            <Trash size={16} />
                          </button>
                        </>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {isAdmin && (
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogContent
            className="w-[95vw] sm:w-full max-w-lg"
            data-testid="tenant-dialog"
          >
            <DialogHeader>
              <DialogTitle>
                {editing ? "Editar Empresa" : "Nova Empresa"}
              </DialogTitle>
              <DialogDescription>
                Configure CNPJ, certificado A1 e parâmetros do worker SEFAZ.
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2">
                <Label htmlFor="cnpj">CNPJ</Label>
                <Input
                  id="cnpj"
                  data-testid="form-cnpj"
                  value={form.cnpj}
                  disabled={!!editing}
                  onChange={(e) => setForm({ ...form, cnpj: e.target.value })}
                  placeholder="00.000.000/0000-00"
                  className="font-mono mt-1.5"
                />
              </div>
              <div className="col-span-2">
                <Label htmlFor="razao">Razão Social</Label>
                <Input
                  id="razao"
                  data-testid="form-razao"
                  value={form.razao_social}
                  onChange={(e) =>
                    setForm({ ...form, razao_social: e.target.value })
                  }
                  className="mt-1.5"
                />
              </div>

              {/* Ajuste 1: Select da UF */}
              <div>
                <Label htmlFor="uf">UF</Label>
                <Select
                  value={form.uf}
                  onValueChange={(v) => setForm({ ...form, uf: v })}
                >
                  <SelectTrigger
                    data-testid="form-uf"
                    className="mt-1.5 font-mono"
                  >
                    <SelectValue placeholder="Selecione..." />
                  </SelectTrigger>
                  <SelectContent>
                    {UFS.map((uf) => (
                      <SelectItem key={uf} value={uf}>
                        {uf}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Ambiente</Label>
                <Select
                  value={form.ambiente}
                  onValueChange={(v) => setForm({ ...form, ambiente: v })}
                >
                  <SelectTrigger data-testid="form-ambiente" className="mt-1.5">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="mock">Mock (demonstração)</SelectItem>
                    <SelectItem value="homologacao">Homologação</SelectItem>
                    <SelectItem value="producao">Produção</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="col-span-2">
                <Label>Grupo</Label>
                <Select
                  value={form.group_id || "none"}
                  onValueChange={(v) =>
                    setForm({ ...form, group_id: v === "none" ? "" : v })
                  }
                >
                  <SelectTrigger data-testid="form-group" className="mt-1.5">
                    <SelectValue placeholder="Selecionar grupo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">— sem grupo —</SelectItem>
                    {groups.map((g) => (
                      <SelectItem key={g.id} value={g.id}>
                        {g.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground mt-1">
                  Apenas usuários do mesmo grupo verão esta empresa.
                </p>
              </div>

              {/* Ajuste 2: Lógica de extração do Certificado */}
              {form.ambiente !== "mock" && (
                <>
                  <div className="col-span-2">
                    <Label htmlFor="cert">Certificado A1 (.pfx / .p12)</Label>
                    <Input
                      id="cert"
                      type="file"
                      accept=".pfx,.p12"
                      data-testid="form-cert"
                      onChange={(e) =>
                        setForm({
                          ...form,
                          cert_file: e.target.files?.[0] || null,
                        })
                      }
                      className="mt-1.5 cursor-pointer file:cursor-pointer"
                    />
                    {editing && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Deixe vazio para manter o certificado atual.
                      </p>
                    )}
                  </div>
                  <div className="col-span-2 flex items-end gap-2">
                    <div className="flex-1">
                      <Label htmlFor="senha">Senha do Certificado</Label>
                      <Input
                        id="senha"
                        type="password"
                        data-testid="form-cert-pwd"
                        value={form.cert_password}
                        onChange={(e) =>
                          setForm({ ...form, cert_password: e.target.value })
                        }
                        className="mt-1.5"
                      />
                    </div>
                    <Button
                      type="button"
                      variant="secondary"
                      onClick={handleExtractCert}
                      disabled={!form.cert_file || extracting}
                    >
                      {extracting ? "Lendo..." : "Extrair Dados"}
                    </Button>
                  </div>
                </>
              )}
              <div className="flex items-center gap-3 col-span-2 pt-2">
                <Switch
                  checked={form.manifestacao_auto}
                  onCheckedChange={(v) =>
                    setForm({
                      ...form,
                      manifestacao_auto: v,
                      download_xml_auto: v ? form.download_xml_auto : false,
                    })
                  }
                  data-testid="form-manifestacao-auto"
                />
                <Label className="cursor-pointer">
                  Realizar Ciência da Operação automaticamente para todas as
                  NF-es de entrada
                </Label>
              </div>
              <div className="flex items-center gap-3 col-span-2">
                <Switch
                  checked={form.download_xml_auto}
                  onCheckedChange={(v) =>
                    setForm({
                      ...form,
                      download_xml_auto: v,
                      manifestacao_auto: v ? true : form.manifestacao_auto,
                    })
                  }
                  data-testid="form-download-xml-auto"
                />
                <Label className="cursor-pointer">
                  Após a Ciência da Operação, buscar automaticamente o XML
                  completo das NF-es de entrada
                </Label>
              </div>
              <div className="flex items-center gap-3 col-span-2">
                <Switch
                  checked={form.ativo}
                  onCheckedChange={(v) => setForm({ ...form, ativo: v })}
                  data-testid="form-ativo"
                />
                <Label className="cursor-pointer">
                  Empresa ativa (worker irá monitorar)
                </Label>
              </div>
            </div>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setOpen(false)}
                data-testid="form-cancel"
              >
                Cancelar
              </Button>
              <Button
                onClick={submit}
                disabled={submitting}
                data-testid="form-submit"
              >
                {submitting ? "Salvando..." : "Salvar"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
};

export default Tenants;
